import { useState } from 'react';
import {
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { switchHome, switchTenant } from '../api/tenants';
import { requestMessage } from '../utils/requestError';

/**
 * Pick which organisation to work in: your own, or one you have been given
 * access to. Switching reloads the app so nothing from the previous
 * organisation stays on screen.
 */
export default function SwitchOrganisationDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const [busy, setBusy] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const tenants = user?.accessible_tenants ?? [];
  const currentId = user?.tenant_id;

  const choose = async (tenantId: number, isHome: boolean) => {
    if (tenantId === currentId) {
      onClose();
      return;
    }
    setBusy(tenantId);
    setError(null);
    try {
      if (isHome) await switchHome();
      else await switchTenant(tenantId);
    } catch (err: unknown) {
      setBusy(null);
      setError(requestMessage(err, 'Could not switch organisation'));
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth sx={{ zIndex: 11500 }}>
      <DialogTitle>Switch organisation</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <List dense>
          {tenants.map((t) => (
            <ListItemButton
              key={t.id}
              selected={t.id === currentId}
              disabled={busy !== null}
              onClick={() => choose(t.id, t.is_home)}
            >
              <ListItemText primary={t.name} secondary={t.is_home ? 'Your organisation' : undefined} />
              {t.id === currentId && <Chip size="small" label="Current" />}
              {busy === t.id && <CircularProgress size={18} sx={{ ml: 1 }} />}
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
}
